/**
 * NumPad — виртуальная цифровая клавиатура (3×4 grid).
 *
 * Банковский паттерн (Apple Lock / Avast / Тинькофф): 1-9, нижний ряд
 * пусто-0-⌫. Тактильная отдача через navigator.vibrate (только mobile).
 *
 * Кнопки 64×64px (WCAG 2.5.5 AAA touch target). На non-touch — work как
 * обычная клавиатура: можно клавишей с ноута если фокус в ARIA-grid.
 */
import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

export interface NumPadProps {
  /** Колбэк на цифру 0-9 */
  onDigit: (digit: string) => void
  /** Колбэк на ⌫ (backspace) */
  onDelete: () => void
  /** Disabled когда submitting */
  disabled?: boolean
  /** Включить keyboard listener (default true) */
  bindKeyboard?: boolean
  className?: string
}

const KEYS: Array<{ value: string; label: string; testid: string } | null> = [
  { value: '1', label: '1', testid: 'numpad-1' },
  { value: '2', label: '2', testid: 'numpad-2' },
  { value: '3', label: '3', testid: 'numpad-3' },
  { value: '4', label: '4', testid: 'numpad-4' },
  { value: '5', label: '5', testid: 'numpad-5' },
  { value: '6', label: '6', testid: 'numpad-6' },
  { value: '7', label: '7', testid: 'numpad-7' },
  { value: '8', label: '8', testid: 'numpad-8' },
  { value: '9', label: '9', testid: 'numpad-9' },
  null, // empty cell под 0
  { value: '0', label: '0', testid: 'numpad-0' },
  { value: 'del', label: '⌫', testid: 'numpad-delete' },
]

function vibrate(ms = 8) {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    try {
      navigator.vibrate?.(ms)
    } catch {
      /* ignore — некоторые browsers throw на multiple vibrate */
    }
  }
}

export function NumPad({ onDigit, onDelete, disabled = false, bindKeyboard = true, className }: NumPadProps) {
  const onDigitRef = useRef(onDigit)
  const onDeleteRef = useRef(onDelete)
  onDigitRef.current = onDigit
  onDeleteRef.current = onDelete

  // Hardware keyboard support: 0-9 + Backspace
  useEffect(() => {
    if (!bindKeyboard || disabled) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        onDigitRef.current(e.key)
      } else if (e.key === 'Backspace') {
        onDeleteRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [bindKeyboard, disabled])

  const handle = (key: { value: string; testid: string }) => {
    if (disabled) return
    vibrate(8)
    if (key.value === 'del') onDelete()
    else onDigit(key.value)
  }

  return (
    <div
      role="group"
      aria-label="Цифровая клавиатура"
      data-testid="numpad"
      className={cn('grid grid-cols-3 gap-3 max-w-xs mx-auto', className)}
    >
      {KEYS.map((key, i) =>
        key ? (
          <button
            key={key.value}
            type="button"
            disabled={disabled}
            onClick={() => handle(key)}
            data-testid={key.testid}
            aria-label={key.value === 'del' ? 'Удалить цифру' : `Цифра ${key.label}`}
            className={cn(
              'w-16 h-16 mx-auto rounded-full',
              'inline-flex items-center justify-center',
              'bg-surface-subtle border border-border',
              'text-text-primary font-mono font-semibold text-2xl',
              'transition-all duration-100',
              'hover:bg-action-secondary-bg-hover',
              'active:scale-95 active:bg-action-secondary-bg-hover',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40',
              'disabled:opacity-40 disabled:cursor-not-allowed',
            )}
          >
            {key.value === 'del' ? (
              <span aria-hidden="true" className="text-2xl leading-none">⌫</span>
            ) : (
              key.label
            )}
          </button>
        ) : (
          <span key={`empty-${i}`} aria-hidden="true" />
        ),
      )}
    </div>
  )
}
